import React from "react";

import { Text } from "@performyard/py-components";

import { getColor } from "../../../../../utils/getColor";

import { BudgetDoughnutChart } from "../BudgetDoughnutChart";

type LegendItem = {
	label: string;
	subtitle: string;
	value: number;
	color: string;
};

type TotalBudgetBreakdownProps = {
	approvedSpend: number;
	submittedSpend: number;
	approvedBudgetCount: number;
	submittedBudgetCount: number;
	notSubmittedEmployeeCount: number;
	employeesApproved: number;
	employeesSubmitted: number;
	notApprovedSpend: number;
};

const formatCurrency = (value: number): string => {
	const sign = value < 0 ? "-" : "";
	const abs = Math.abs(value);
	if (abs >= 1_000_000) {
		const millions = abs / 1_000_000;
		const formatted = millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(2);
		return `${sign}$${formatted}M`;
	}
	if (abs >= 1_000) {
		const thousands = abs / 1_000;
		const formatted = thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(2);
		return `${sign}$${formatted}K`;
	}
	return `${sign}$${abs.toLocaleString()}`;
};

const formatFullCurrency = (value: number): string => `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const pluralize = (count: number, singular: string): string => (count === 1 ? singular : `${singular}s`);

const formatAvgPerEmployee = (spend: number, count: number): string => {
	if (count === 0) return "$0";
	return `$${Math.round(spend / count).toLocaleString("en-US")}`;
};

export const TotalBudgetBreakdown: React.FC<TotalBudgetBreakdownProps> = ({
	approvedSpend,
	submittedSpend,
	approvedBudgetCount,
	submittedBudgetCount,
	notSubmittedEmployeeCount,
	employeesApproved,
	employeesSubmitted,
	notApprovedSpend,
}) => {
	const notSubmittedSpend = Math.max(notApprovedSpend - submittedSpend, 0);
	const totalBudget = notApprovedSpend + approvedSpend;

	const approvedColor = getColor("green", 5);
	const submittedColor = getColor("yellow", 2);
	const notSubmittedColor = getColor("neutral", 2);

	const legendItems: LegendItem[] = [
		{
			label: "Approved Spend",
			subtitle: `${formatAvgPerEmployee(approvedSpend, employeesApproved)} avg per employee \u2022 ${approvedBudgetCount} ${pluralize(approvedBudgetCount, "budget")} approved`,
			value: approvedSpend,
			color: approvedColor,
		},
		{
			label: "Submitted Spend",
			subtitle: `${formatAvgPerEmployee(submittedSpend, employeesSubmitted)} avg per employee \u2022 ${submittedBudgetCount} ${pluralize(submittedBudgetCount, "budget")} submitted`,
			value: submittedSpend,
			color: submittedColor,
		},
		{
			label: "Not Submitted",
			subtitle: `Not yet assigned \u2022 ${notSubmittedEmployeeCount.toLocaleString("en-US")} ${pluralize(notSubmittedEmployeeCount, "employee")}`,
			value: notSubmittedSpend,
			color: notSubmittedColor,
		},
	];

	return (
		<div className="box-border flex h-[296px] w-1/2 min-w-0 flex-col rounded-lg border border-solid border-neutral-2 bg-neutral-0 p-4">
			<Text size="paragraph-3" weight="semi-bold">
				Total Budget Breakdown
			</Text>
			<div className="mt-4 flex flex-1 items-center gap-8">
				<BudgetDoughnutChart data={legendItems} />
				<div className="flex flex-1 flex-col">
					{legendItems.map((item) => (
						<div key={item.label} className="flex items-start gap-3 py-2">
							<div
								className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
								style={{ backgroundColor: item.color }}
							/>
							<div className="flex-1">
								<Text color="neutral-9" size="caption" weight="semi-bold">
									{item.label}
								</Text>
								<Text color="neutral-9" size="caption" weight="regular">
									{item.subtitle}
								</Text>
							</div>
							<Text color="neutral-9" size="paragraph-3" weight="semi-bold">
								{formatCurrency(item.value)}
							</Text>
						</div>
					))}
					<div className="mt-2 flex items-center border-t border-solid border-neutral-2 pt-2">
						<Text size="paragraph-3" weight="semi-bold">
							Total Budget
						</Text>
						<div className="ml-auto">
							<Text size="paragraph-3" weight="bold">
								{formatFullCurrency(totalBudget)}
							</Text>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
